import { useParams, Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import SEO from "../components/SEO";
import { useSSRData } from "../contexts/SSRDataContext";
import { trackServerPageView, trackButtonClick, setupPageTimeTracking, trackEvent, getUtmParams } from "../lib/analytics";
import { buildDscsaFaqJsonLd, DSCSA_HUB_FAQ } from "@shared/dscsaContent";
import { DSCSA_HUB_META_DESCRIPTION } from "@shared/publicPageSeoBuilders";

const DSCSA_HUB_PATH = "/resources/dscsa-compliance";

// TODO(post-2027-11-27): After the Nov 27, 2027 small-dispenser deadline passes,
//   update all urgency framing in this file to post-deadline messaging:
//   - Hero countdown chip → "DSCSA is fully in effect — are you compliant?"
//   - Remove daysLeft countdown; replace with compliance-check CTA
//   - Stats bar: swap "Nov 27, 2027 — Extended deadline" for an enforcement-status stat
//   - Checklist bullet "Phased enforcement timeline through Nov 27, 2027" → update copy
//   - Mobile sticky bar: update the deadline label
//   - Mid-page CTA banner: keep the educational checklist and account bridge current
//   - SEO title/description in HubPage <SEO>: remove "deadline" urgency, add "fully enforceable"
//   Also regenerate public/dscsa-checklist.pdf via: node scripts/generate-dscsa-checklist.mjs
const SMALL_DISPENSER_DEADLINE = new Date("2027-11-27T00:00:00Z");
interface SubPageSummary {
  slug: string;
  title: string;
  description: string;
}

interface DscsaSubPage {
  slug: string;
  title: string;
  metaTitle: string;
  description: string;
  content: string;
  faq: Array<{ q: string; a: string }>;
}

const STATIC_SUB_PAGES: SubPageSummary[] = [
  {
    slug: "serialization",
    title: "DSCSA Serialization Requirements for Pharmaceutical Distributors",
    description: "Learn what DSCSA serialization requires, who must comply, and the phased enforcement timeline — with the extended small-dispenser deadline on November 27, 2027.",
  },
  {
    slug: "t2-documents",
    title: "DSCSA T2 Documents — Transaction Information & Statement",
    description: "Every DSCSA drug transfer requires a Transaction Information (TI) and Transaction Statement (TS). Learn what each must contain and how long to retain records.",
  },
  {
    slug: "track-and-trace",
    title: "DSCSA Track-and-Trace — End-to-End Pharmaceutical Traceability",
    description: "Learn how DSCSA interoperability requirements affect your pharmacy, how the Verification Router Service (VRS) works, and the phased compliance timeline.",
  },
  {
    slug: "wholesale-distributor-requirements",
    title: "DSCSA Requirements for Wholesale Drug Distributors",
    description: "Wholesale distributors have important DSCSA responsibilities. Learn ATP verification, suspect product reporting, and VAWD accreditation requirements.",
  },
  {
    slug: "saleable-returns",
    title: "DSCSA Saleable Returns — Requirements for Returned Pharmaceutical Products",
    description: "DSCSA imposes verification requirements before returned drug products can be resold. Learn what wholesale distributors and pharmacies must do to comply.",
  },
  {
    slug: "trading-partner-verification",
    title: "DSCSA Trading Partner Verification — How to Confirm Your Suppliers Are Authorized",
    description: "Learn how to verify supplier licenses, what qualifies as an Authorized Trading Partner, and the red flags that indicate an unauthorized pharmaceutical source.",
  },
];

function renderMarkdown(content: string): string {
  const lines = content.trim().split(/\r?\n/);
  const rendered: string[] = [];
  const paragraphLines: string[] = [];
  let listType: "ol" | "ul" | null = null;
  let listItems: string[] = [];

  const renderInline = (text: string) =>
    text.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-800">$1</strong>');

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    rendered.push(
      `<p class="text-gray-600 text-sm leading-relaxed mb-4">${paragraphLines
        .map(renderInline)
        .join("\n")}</p>`
    );
    paragraphLines.length = 0;
  };

  const flushList = () => {
    if (!listType) return;
    rendered.push(`<${listType}>${listItems.join("")}</${listType}>`);
    listType = null;
    listItems = [];
  };

  for (const line of lines) {
    const trimmedLine = line.trim();

    if (!trimmedLine) {
      flushParagraph();
      flushList();
      continue;
    }

    const orderedItem = trimmedLine.match(/^\d+\.\s+(.+)$/);
    const unorderedItem = trimmedLine.match(/^- (.+)$/);
    if (orderedItem || unorderedItem) {
      flushParagraph();

      const nextListType = orderedItem ? "ol" : "ul";
      if (listType && listType !== nextListType) {
        flushList();
      }
      listType = nextListType;

      const itemClass = orderedItem ? "list-decimal" : "list-disc";
      const itemText = orderedItem ? orderedItem[1] : unorderedItem![1];
      listItems.push(
        `<li class="ml-4 ${itemClass} text-gray-600 text-sm leading-relaxed mb-1">${renderInline(itemText)}</li>`
      );
      continue;
    }

    flushList();

    const heading2 = trimmedLine.match(/^## (.+)$/);
    if (heading2) {
      flushParagraph();
      rendered.push(`<h2 class="text-xl font-bold text-primary mt-8 mb-3">${renderInline(heading2[1])}</h2>`);
      continue;
    }

    const heading3 = trimmedLine.match(/^### (.+)$/);
    if (heading3) {
      flushParagraph();
      rendered.push(`<h3 class="text-base font-semibold text-gray-800 mt-5 mb-2">${renderInline(heading3[1])}</h3>`);
      continue;
    }

    paragraphLines.push(trimmedLine);
  }

  flushParagraph();
  flushList();
  return rendered.join("\n");
}

function FAQSection({ items }: { items: Array<{ q: string; a: string }> }) {
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3">
            <h3 className="font-semibold text-primary text-sm mb-2">{f.q}</h3>
            <p className="text-gray-600 text-sm leading-relaxed">{f.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Checklist form — shared between the hero and any standalone usage */
function ChecklistForm() {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setError("Please enter your email address.");
      trackEvent("dscsa_form_error", { reason: "missing_email", page: DSCSA_HUB_PATH });
      return;
    }
    if (!emailRegex.test(email.trim())) {
      setError("Please enter a valid email address.");
      trackEvent("dscsa_form_error", { reason: "invalid_email", page: DSCSA_HUB_PATH });
      return;
    }

    setLoading(true);
    try {
      const utm = getUtmParams();
      const res = await fetch("/api/dscsa/request-resource", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          phone: phone.trim() || undefined,
          utmSource: utm.utmSource,
          utmMedium: utm.utmMedium,
          utmCampaign: utm.utmCampaign,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        const reason = data.error || "server_error";
        setError(data.error || "Something went wrong. Please try again.");
        trackEvent("dscsa_form_error", { reason, page: DSCSA_HUB_PATH });
      } else {
        setSubmitted(true);
        trackEvent("generate_lead", { method: "dscsa_checklist", page: DSCSA_HUB_PATH });
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
      trackEvent("dscsa_form_error", { reason: "network_error", page: DSCSA_HUB_PATH });
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div role="alert" className="bg-white rounded-2xl border border-green-100 shadow-sm p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-green-600" viewBox="0 0 24 24" fill="none">
            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="text-xl font-bold text-primary mb-2">Checklist Sent!</h3>
        <p className="text-gray-600 text-sm leading-relaxed mb-5">
          Check your inbox — the DSCSA Compliance Checklist PDF has been sent. If you don't see it, check your spam folder.
        </p>
        <Link
          to="/register"
          onClick={() => trackButtonClick("Open a Free Account", "checklist-post-submit")}
          className="inline-flex items-center gap-2 bg-accent hover:bg-blue-600 text-white rounded-lg px-6 py-2.5 font-semibold text-sm transition-colors"
        >
          Open a Free Account
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-white/20 shadow-xl p-6 sm:p-8">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="hub-dscsa-email" className="block text-sm font-medium text-gray-700 mb-1.5">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            id="hub-dscsa-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@yourpharmacy.com"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
          />
        </div>

        <div>
          <label htmlFor="hub-dscsa-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
            Phone <span className="text-gray-600 font-normal">(optional)</span>
          </label>
          <input
            id="hub-dscsa-phone"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 000-0000"
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition"
          />
        </div>

        {error && (
          <p role="alert" className="text-red-600 text-sm bg-red-50 border border-red-100 rounded-lg px-4 py-3">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          onClick={() => !loading && trackButtonClick("Send Me the Checklist", "hub-checklist-form")}
          className="w-full bg-accent hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg py-3.5 font-semibold text-base transition-all duration-200 hover:shadow-lg hover:shadow-accent/25"
        >
          {loading ? "Sending…" : "Send Me the Checklist"}
        </button>

        <p className="text-center text-xs text-gray-600">
          We'll email the PDF after submission. No spam, no account required.
        </p>
      </form>
    </div>
  );
}
// ── SOCIAL PROOF DATA ────────────────────────────────────────────────────────
// TODO(before-launch): Replace these placeholder entries with real customer quotes.
// Each entry only needs: a 1–2 sentence quote, first name + last initial, role, and city/state.
// Collect via post-checklist follow-up email or sales call notes.
// When real quotes are ready, update the two objects below — no other code changes needed.
// If you prefer a stat fallback instead of testimonials, set this array to [] and pass a
// pharmacyCount prop to <SocialProofBand> (the component handles both cases).
interface Testimonial {
  quote: string;
  authorName: string;
  role: string;
  location: string;
}

// Empty until real, consented customer quotes are collected and approved.
// To add a testimonial: append an entry with a verified quote, the customer's
// first name + last initial, their role, and their city/state.
// The social proof band will not render at all while this array is empty.
const DSCSA_TESTIMONIALS: Testimonial[] = [];

// Verified trust facts shown in the social proof band when no customer testimonials
// are available yet. Every claim here is also asserted elsewhere on the page.
const TRUST_FACTS = [
  {
    icon: (
      <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    heading: "NABP VAWD Accredited",
    body: "Verified Accredited Wholesale Distributor — the highest accreditation a drug wholesaler can earn from the National Association of Boards of Pharmacy.",
  },
  {
    icon: (
      <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    heading: "T2 documentation explained",
    body: "Learn how Transaction Information (TI) and Transaction Statements (TS) fit into a DSCSA recordkeeping workflow.",
  },
  {
    icon: (
      <svg className="w-5 h-5 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
      </svg>
    ),
    heading: "Licensed-business review",
    body: "Account credentials are reviewed before activation so eligible businesses can discuss the right ordering and workflow next steps.",
  },
];

/** Renders between the hero and the stats bar.
 *  Shows testimonial cards when real verified quotes are in DSCSA_TESTIMONIALS;
 *  falls back to a verified-facts trust strip (accreditations already shown on this page)
 *  when no testimonials are available. */
function SocialProofBand({ testimonials }: { testimonials: Testimonial[] }) {
  if (testimonials.length > 0) {
    return (
      <section className="py-10 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-xs font-semibold text-gray-600 uppercase tracking-widest mb-6">
            What pharmacies and clinics say about Premier Pharma
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {testimonials.slice(0, 2).map((t, i) => (
              <blockquote
                key={i}
                className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-col gap-4 shadow-sm"
              >
                <svg
                  className="w-6 h-6 text-accent/30 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
                </svg>
                <p className="text-gray-700 text-sm leading-relaxed flex-1 italic">"{t.quote}"</p>
                <footer className="text-xs text-gray-500">
                  <span className="font-semibold text-gray-700 not-italic">{t.authorName}</span>
                  {" · "}
                  {t.role}
                  {" · "}
                  {t.location}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>
    );
  }

  // Fallback: verified-facts trust strip using only claims asserted elsewhere on this page
  return (
    <section className="py-8 bg-gray-50 border-b border-gray-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-xs font-semibold text-gray-600 uppercase tracking-widest mb-6">
          Why pharmacies and clinics choose Premier Pharma for DSCSA compliance
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {TRUST_FACTS.map((fact) => (
            <div key={fact.heading} className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                {fact.icon}
                <span className="text-sm font-semibold text-primary">{fact.heading}</span>
              </div>
              <p className="text-gray-500 text-xs leading-relaxed">{fact.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HubPage({ subPages }: { subPages: SubPageSummary[] }) {
  const location = useLocation();
  // Countdown to Nov 27, 2027 — initialized to null on server, set after hydration
  const [daysLeft, setDaysLeft] = useState<number | null>(null);

  useEffect(() => {
    const now = new Date();
    const days = Math.ceil((SMALL_DISPENSER_DEADLINE.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    setDaysLeft(days > 0 ? days : 0);
  }, []);

  // Scroll to the checklist form when navigating via the #checklist-form hash
  useEffect(() => {
    if (location.hash === "#checklist-form") {
      const el = document.getElementById("checklist-form");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [location.hash]);

  return (
    <>
      <SEO
        title="DSCSA Compliance Resource Hub"
        description={DSCSA_HUB_META_DESCRIPTION}
        path={DSCSA_HUB_PATH}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "DSCSA Compliance", path: DSCSA_HUB_PATH },
        ]}
        jsonLd={buildDscsaFaqJsonLd(DSCSA_HUB_FAQ)}
      />

      {/* ── HERO + ABOVE-THE-FOLD FORM ──────────────────────────────────── */}
      <section
        id="checklist-form"
        className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-14"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <span className="text-white/60">Resources</span>
            <span className="mx-2">/</span>
            <span className="text-white">DSCSA Compliance</span>
          </nav>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            {/* Left — headline + urgency */}
            <div>
              {/* Urgency deadline chip */}
              <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/40 text-red-200 text-xs font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full mb-4">
                <span className="w-2 h-2 rounded-full bg-red-400 animate-pulse inline-block" />
                {daysLeft !== null
                  ? `${daysLeft} days until the Nov 27, 2027 deadline`
                  : "Nov 27, 2027 — Small Dispenser Deadline"}
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-4">
                DSCSA Compliance: Is Your Pharmacy Ready?
              </h1>
              <p className="text-white/80 text-base leading-relaxed mb-5">
                FDA guidance requires trading partners to work with <strong className="text-white">authorized partners</strong> and maintain processes for <strong className="text-white">suspect or illegitimate product</strong>. If your pharmacy or clinic has 25 or fewer full-time employees, the current small-dispenser date for EPCIS interoperability is <strong className="text-white">November 27, 2027</strong>; other DSCSA responsibilities still apply. Use this hub as an educational starting point and verify requirements for your business with the FDA or a qualified advisor.
              </p>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-3 mb-6">
                {[
                  "Educational guidance for teams",
                  "T2 recordkeeping explained",
                  "Authorized-partner checks",
                ].map((s) => (
                  <span key={s} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 text-white/90 text-xs font-medium px-3 py-1 rounded-full">
                    <svg className="w-3.5 h-3.5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {s}
                  </span>
                ))}
              </div>

              {/* What's inside the checklist */}
              <p className="text-white/70 text-sm font-medium uppercase tracking-wide mb-2">What's in the free checklist:</p>
              <ul className="space-y-2 text-sm text-white/75">
                {[
                  "Phased enforcement timeline through Nov 27, 2027",
                  "Step-by-step serialization verification checklist",
                  "T2 documentation requirements and 6-year retention rules",
                  "Common DSCSA failure points and how to fix them",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-6">
                <a
                  href="#resources"
                  onClick={() => trackButtonClick("Browse Resources", "hero")}
                  className="inline-flex items-center text-sm text-white/60 hover:text-white transition-colors"
                >
                  Browse compliance guides ↓
                </a>
              </div>
            </div>

            {/* Right — email capture form (above the fold) */}
            <div>
              <p className="text-white/80 text-sm font-semibold mb-3">See Exactly Where You Stand</p>
              <ChecklistForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── SOCIAL PROOF BAND ─────────────────────────────────────────────── */}
      <SocialProofBand testimonials={DSCSA_TESTIMONIALS} />

      {/* ── STATS BAR ──────────────────────────────────────────────────────── */}
      <section className="py-6 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-center">
            {[
              { stat: "6 Years", label: "Transaction record retention reference" },
              { stat: "Nov 27, 2027", label: "EPCIS date for eligible small dispensers" },
              { stat: "3 Steps", label: "A practical readiness review" },
              { stat: "Free", label: "Checklist download — no account needed" },
            ].map((s, i) => (
              <div key={i} className="py-3">
                <div className="text-xl lg:text-2xl font-bold text-primary">{s.stat}</div>
                <div className="text-gray-500 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACCOUNT BRIDGE — above resource cards ── */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 sm:p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div>
                <h2 className="text-xl font-bold text-primary mb-2">Ready to put the guidance into a workflow?</h2>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  The free Premier Pharma account includes starter compliance software after business verification. It can help your team organize transaction and receiving workflows; it is not a compliance certification or substitute for official guidance.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  {[
                    "Free account with no credit card or purchase required",
                    "Included starter compliance software",
                    "Credential review typically takes 1–2 business days",
                    "Optional paid products for expanded workflows",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex flex-col items-center gap-4">
                <a
                  href="#checklist-form"
                  onClick={() => trackButtonClick("Get Free DSCSA Checklist", "conversion-panel")}
                  className="w-full block text-center bg-accent hover:bg-blue-600 text-white rounded-xl px-6 py-4 font-semibold text-sm transition-colors"
                >
                  Get Free DSCSA Checklist
                </a>
                <Link
                  to="/register"
                  onClick={() => trackButtonClick("Open a Wholesale Account", "conversion-panel")}
                  className="text-sm text-accent hover:underline font-medium"
                >
                  Ready to compare the account offer? →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RESOURCE TOPIC CARDS ───────────────────────────────────────────── */}
      <section id="resources" className="py-12 bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-primary mb-2">Not sure where to start? Read these guides.</h2>
          <p className="text-gray-500 text-sm mb-6">
            Detailed guides for every DSCSA requirement — written for pharmacists, not lawyers.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {subPages.map((page) => (
              <Link
                key={page.slug}
                to={`${DSCSA_HUB_PATH}/${page.slug}`}
                onClick={() => trackButtonClick(page.title, "resource-card")}
                className="bg-white border border-gray-200 rounded-xl p-5 hover:border-accent/40 hover:shadow-md transition-all group"
              >
                <h3 className="font-bold text-primary text-base mb-2 group-hover:text-accent transition-colors leading-snug">
                  {page.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{page.description}</p>
                <div className="mt-3 text-accent text-xs font-medium">Read guide →</div>
              </Link>
            ))}
          </div>

          {/* Mid-page CTA banner */}
          <div className="mt-10 bg-red-50 border border-red-200 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-800 mb-1">A practical next step</p>
              <p className="text-sm text-red-700 leading-relaxed">
                The FDA extended the small-dispenser deadline to November 27, 2027. The exemption is automatic for eligible small dispensers, but authorized-partner checks and suspect-product procedures remain important. Use the checklist to organize a review, then see the free account and included starter software when you are ready.
              </p>
            </div>
            <a
              href="#checklist-form"
              onClick={() => trackButtonClick("Get Free Checklist", "midpage-cta")}
              className="flex-shrink-0 bg-accent hover:bg-blue-600 text-white rounded-xl px-6 py-3.5 font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Get Free Checklist
            </a>
            <Link
              to="/lp/dscsa-compliance"
              onClick={() => trackButtonClick("Check DSCSA Readiness", "midpage-cta")}
              className="flex-shrink-0 border border-accent text-accent hover:bg-accent/5 rounded-xl px-6 py-3.5 font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Check DSCSA Readiness
            </Link>
          </div>
        </div>
      </section>

      <section aria-labelledby="dscsa-faq-heading" className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="dscsa-faq-heading" className="text-2xl font-bold text-primary mb-2">
            DSCSA Frequently Asked Questions
          </h2>
          <p className="text-gray-500 text-sm mb-6">
            Key requirements, deadlines, and enforcement considerations for pharmacies.
          </p>
          <FAQSection items={DSCSA_HUB_FAQ} />
        </div>
      </section>

      {/* ── VIDEO — DSCSA readiness explainer (below resource cards) ───────── */}
      <section className="py-14 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold text-primary mb-3">
            Watch: How to Close Every DSCSA Gap Before Your Next Inspection
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            See how a structured workflow can help teams organize serialization verification, T2 documentation, and EPCIS interoperability work. Product capabilities and eligibility should be confirmed during the account review.
          </p>
          {/* 16:9 aspect-ratio wrapper — matches the previous <video> layout */}
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gray-100 relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe
              src="https://app.heygen.com/embeds/e90f478a7f07400a82b79d6e97b87bd8"
              title="Premier Pharma DSCSA Readiness overview video"
              allow="encrypted-media; fullscreen"
              allowFullScreen
              className="absolute inset-0 w-full h-full block"
              style={{ border: 0 }}
            />
          </div>
        </div>
      </section>

      {/* ── MOBILE PERSISTENT CTA ──────────────────────────────────────────── */}
      <div className="fixed bottom-0 inset-x-0 md:hidden z-40 bg-white border-t border-gray-200 shadow-lg px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-primary truncate">DSCSA readiness resources</p>
          <p className="text-xs text-gray-500">Free checklist — no account needed</p>
        </div>
        <a
          href="#checklist-form"
          onClick={() => trackButtonClick("Get Free Checklist", "mobile-sticky-cta")}
          className="flex-shrink-0 bg-accent hover:bg-blue-600 text-white rounded-lg px-4 py-2.5 font-semibold text-sm transition-colors"
        >
          Get Checklist
        </a>
      </div>
    </>
  );
}

function SubPage({ page }: { page: DscsaSubPage }) {
  return (
    <>
      <SEO
        title={page.metaTitle}
        description={page.description}
        path={`${DSCSA_HUB_PATH}/${page.slug}`}
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "DSCSA Compliance", path: DSCSA_HUB_PATH },
          { name: page.title, path: `${DSCSA_HUB_PATH}/${page.slug}` },
        ]}
        jsonLd={page.faq?.length ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: page.faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        } : undefined}
      />

      <section className="bg-gradient-to-br from-primary via-[#0a1e4a] to-[#0d2d6b] text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-xs text-white/70 mb-3">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2">/</span>
            <Link to={DSCSA_HUB_PATH} className="hover:text-white">DSCSA Compliance</Link>
            <span className="mx-2">/</span>
            <span className="text-white">{page.title.split("—")[0].trim()}</span>
          </nav>
          <h1 className="text-2xl lg:text-3xl font-bold leading-tight">{page.title}</h1>
          <p className="text-white/75 text-sm mt-3 max-w-2xl leading-relaxed">{page.description}</p>
        </div>
      </section>

      <section className="py-12 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div
                className="prose prose-sm max-w-none bg-white border border-gray-200 rounded-xl p-6 text-gray-700"
                dangerouslySetInnerHTML={{ __html: renderMarkdown(page.content) }}
              />

              <div className="mt-8">
                <h2 className="text-xl font-bold text-primary mb-4">Frequently Asked Questions</h2>
                <FAQSection items={page.faq} />
              </div>

              <div className="mt-8 bg-accent/5 border border-accent/20 rounded-xl p-5">
                <p className="text-sm text-gray-600 leading-relaxed">
                  <strong className="text-primary">Disclaimer:</strong> This guide is for informational purposes only and does not constitute legal or regulatory advice. DSCSA requirements are complex and evolving. Consult your legal counsel or compliance advisor for guidance specific to your situation. For official requirements, refer to the FDA's DSCSA guidance documents at fda.gov.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="bg-white border border-gray-200 rounded-xl p-5 sticky top-20">
                <h3 className="font-bold text-primary mb-3 text-sm">DSCSA Resources</h3>
                <ul className="space-y-2">
                  {STATIC_SUB_PAGES.map((sp) => (
                    <li key={sp.slug}>
                      <Link
                        to={`${DSCSA_HUB_PATH}/${sp.slug}`}
                        className={`text-sm block py-1 transition-colors leading-snug ${sp.slug === page.slug ? "text-accent font-semibold" : "text-gray-600 hover:text-accent"}`}
                      >
                        {sp.title.split("—")[0].trim().split(" ").slice(0, 4).join(" ")}…
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-gray-100 mt-4 pt-4 space-y-2">
                  <Link to={`${DSCSA_HUB_PATH}#checklist-form`} onClick={() => trackButtonClick("Free DSCSA Checklist", "sidebar")} className="block bg-accent hover:bg-blue-600 text-white text-center rounded-lg px-4 py-2.5 text-sm font-medium transition-colors">
                    Free DSCSA Checklist
                  </Link>
                  <Link to="/register" onClick={() => trackButtonClick("Open Account", "sidebar")} className="block bg-white border border-gray-300 text-primary text-center rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-gray-50 transition-colors">
                    Open Account
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default function DscsaResources() {
  const params = useParams<{ subpage?: string }>();
  const subpageSlug = params.subpage;
  const { seoData } = useSSRData();
  const [fetchedPage, setFetchedPage] = useState<DscsaSubPage | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const [fetching, setFetching] = useState(false);

  const needsFetch =
    subpageSlug &&
    !(seoData?.type === "dscsa-subpage" && seoData.page?.slug === subpageSlug);

  useEffect(() => {
    trackServerPageView("/resources/dscsa-compliance", "dscsa_resources_page_view");
    return setupPageTimeTracking("/resources/dscsa-compliance");
  }, []);

  useEffect(() => {
    if (!needsFetch) return;
    setFetching(true);
    setFetchError(false);
    setFetchedPage(null);
    fetch(`/api/seo/dscsa/${encodeURIComponent(subpageSlug!)}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((data) => {
        setFetchedPage(data.page || null);
        setFetching(false);
      })
      .catch(() => {
        setFetchError(true);
        setFetching(false);
      });
  }, [subpageSlug, needsFetch]);

  if (seoData?.type === "dscsa-hub" && !subpageSlug) {
    return <HubPage subPages={seoData.subPages || STATIC_SUB_PAGES} />;
  }

  if (seoData?.type === "dscsa-subpage" && seoData.page && seoData.page.slug === subpageSlug) {
    return <SubPage page={seoData.page} />;
  }

  if (!subpageSlug) {
    return <HubPage subPages={STATIC_SUB_PAGES} />;
  }

  if (fetching) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>
    );
  }

  if (fetchedPage) {
    return <SubPage page={fetchedPage} />;
  }

  if (fetchError) {
    return (
      <>
        <SEO title="Page Not Found" noindex />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-2xl font-bold text-primary mb-2">Page not found</h1>
          <Link to={DSCSA_HUB_PATH} className="bg-accent text-white px-5 py-2 rounded-lg font-medium mt-4">DSCSA Hub</Link>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-gray-500">Loading…</div>
  );
}
